export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

const SCREEN_BUFFER_SIZE = 80;

/**
 * Utility function to get the position classes for the tooltip.
 *
 * @param {TooltipPosition} position - The position of the tooltip (top, bottom, left, right).
 * @returns {string} - The CSS classes for the specified position.
 */
export const getTooltipPositionClasses = (
  position: TooltipPosition
): string => {
  const positions = {
    top: 'bottom-full left-1/2 transform -translate-x-1/2 -translate-y-6 mb-1',
    bottom: 'top-full left-1/2 transform -translate-x-1/2 translate-y-6 mt-1',
    left: 'right-full top-1/2 transform -translate-y-1/2 -translate-x-6 mr-1',
    right: 'left-full top-1/2 transform -translate-y-1/2 translate-x-6 ml-1'
  };

  return positions[position];
};

/**
 * Utility function to get the arrow classes for the tooltip.
 *
 * @param {TooltipPosition} position - The position of the tooltip (top, bottom, left, right).
 * @returns {string} - The CSS classes for the arrow based on the specified position.
 */
export const getTooltipArrowClasses = (position: TooltipPosition): string => {
  const arrows = {
    top: 'bottom-[-24px] left-1/2 transform -translate-x-1/2 border-t-grey900 border-l-transparent border-r-transparent border-b-transparent border-[12px]',
    bottom:
      'top-[-24px] left-1/2 transform -translate-x-1/2 border-b-grey900 border-l-transparent border-r-transparent border-t-transparent border-[12px]',
    left: 'right-[-24px] top-1/2 transform -translate-y-1/2 border-l-grey900 border-t-transparent border-b-transparent border-r-transparent border-[12px]',
    right:
      'left-[-24px] top-1/2 transform -translate-y-1/2 border-r-grey900 border-t-transparent border-b-transparent border-l-transparent border-[12px]'
  };

  return arrows[position];
};

/**
 * Checks if tooltip would be cut off by viewport edges and returns adjusted position if needed
 *
 * @param {DOMRect | null} tooltipRect - Bounding rectangle of tooltip element
 * @param {DOMRect | null} triggerRect - Bounding rectangle of trigger element
 * @param {TooltipPosition} position - Current tooltip position
 * @returns {TooltipPosition} Adjusted position to avoid viewport cutoff
 */
export const getAdjustedPosition = (
  tooltipRect: DOMRect | null,
  triggerRect: DOMRect | null,
  position: TooltipPosition = 'top'
): TooltipPosition => {
  if (!tooltipRect || !triggerRect) {
    return position;
  }

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  let newPosition = position;

  if (
    (position === 'left' &&
      triggerRect.left < tooltipRect.width + SCREEN_BUFFER_SIZE) ||
    (position === 'right' &&
      triggerRect.right + tooltipRect.width + SCREEN_BUFFER_SIZE >
        viewportWidth)
  ) {
    newPosition = position === 'left' ? 'right' : 'left';
  }

  if (
    (position === 'top' &&
      triggerRect.top < tooltipRect.height + SCREEN_BUFFER_SIZE) ||
    (position === 'bottom' &&
      triggerRect.bottom + tooltipRect.height + SCREEN_BUFFER_SIZE >
        viewportHeight)
  ) {
    newPosition = position === 'top' ? 'bottom' : 'top';
  }

  return newPosition;
};

type MobileTooltipPositionStyleProps = {
  position: TooltipPosition;
  windowWidth: number;
  triggerElement: HTMLDivElement;
};

/*
 * Function to get the position style for the mobile tooltip.
 * Necessary because we require dynamic widths based on the window, which is not possible with Tailwind alone.
 *
 * @param {TooltipPosition} position - The position of the tooltip (top, bottom).
 * @param {number} windowWidth - The width of the window.
 * @param {HTMLDivElement} triggerElement - The trigger element for the tooltip.
 * @returns {object} - The style object for the mobile tooltip position.
 */
export const getMobileTooltipPositionStyle = (
  props: MobileTooltipPositionStyleProps
) => {
  const { position, windowWidth, triggerElement } = props;

  return {
    width: `${windowWidth - 40}px`,
    left: -1 * ((triggerElement.getBoundingClientRect().x as number) - 20),
    transform: 'none',

    ...(position === 'top'
      ? {
          bottom: '44px'
        }
      : {}),

    ...(position === 'bottom'
      ? {
          top: '44px'
        }
      : {})
  };
};

type TooltipArrowPositionStyleProps = {
  position: TooltipPosition;
  triggerElement: HTMLDivElement;
};

/*
 * Function to get the position style for the mobile tooltip arrow.
 * Necessary because we require dynamic position based on the trigger element, which is not possible with Tailwind alone.
 *
 * @param {TooltipPosition} position - The position of the tooltip (top, bottom).
 * @param {HTMLDivElement} triggerElement - The trigger element for the tooltip.
 * @returns {object} - The style object for the mobile tooltip arrow position.
 */
export const getMobileTooltipArrowPositionStyle = (
  props: TooltipArrowPositionStyleProps
) => {
  const { position, triggerElement } = props;

  return {
    left: (triggerElement.getBoundingClientRect().x as number) - 24,
    transform: 'none',
    ...(position === 'top'
      ? {
          bottom: '-24px'
        }
      : {}),

    ...(position === 'bottom'
      ? {
          top: '-24px'
        }
      : {})
  };
};
